import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { NotificationCenter } from '@/modules/notification-center';
import { getRoutePath, useIsOnboardingOrInvitationPage } from '@/routes/util';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import {
  cn,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from '@kognitos/lattice';

import { FEATURE_FLAGS } from '@/shared/types/features';
import { useGateValue } from '@statsig/react-bindings';
import { useIsFinanceWorkspace } from '@/shared/hooks/use-is-finance-workspace';
import OrganizationSwitcher from './OrganizationSwitcher';
import SidebarBrandRow from './SidebarBrandRow';
import SidebarGroupHeading from './SidebarGroupHeading';
import SidebarNavItem from './SidebarNavItem';
import UserDropdown from './UserDropdown';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import { useSidebarHoverOverlay } from './hooks/useSidebarHoverOverlay';
import { getSidebarNavGroups } from './nav-config';

export function AppSidebar() {
  const { state } = useSidebar();
  const isOnboardingOrInvitationPage = useIsOnboardingOrInvitationPage();

  const { t } = useTranslation('navigation');
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const isCollapsed = state === 'collapsed';
  const sidebarRef = useRef<HTMLDivElement>(null);

  const isNotificationsEnabled = useGateValue(
    FEATURE_FLAGS.ENABLE_NOTIFICATIONS_IN_UI,
  );

  const isAgentBuilderEnabled = useGateValue(
    FEATURE_FLAGS.ENABLE_AGENT_BUILDER,
  );

  const isAgentAppsEnabled = useGateValue(FEATURE_FLAGS.ENABLE_AGENT_APPS);

  const isWorkbenchEnabled = useGateValue(FEATURE_FLAGS.ENABLE_NEW_WORKBENCH);

  const isCartographerEnabled = useGateValue(FEATURE_FLAGS.ENABLE_CARTOGRAPHER);

  const isConsistentLayout = useGateValue(FEATURE_FLAGS.CONSISTENT_LAYOUT);

  // `useIsFinanceWorkspace` folds in both the `enable_finance_workspace` gate
  // and the workspace-type check, and is false while the lookup is loading or
  // errors — so finance-only entries are gated by construction and never flash
  // for an unknown workspace.
  const { isFinanceWorkspace } = useIsFinanceWorkspace();
  const variant = isFinanceWorkspace ? 'finance' : 'regular';

  const navGroups = getSidebarNavGroups(variant).map(group => ({
    ...group,
    items: group.items.filter(
      item =>
        item.key !== 'guidance-center' &&
        (item.key !== 'agents' || isAgentBuilderEnabled) &&
        (item.key !== 'apps' || isAgentAppsEnabled) &&
        (item.key !== 'workbench' || isWorkbenchEnabled) &&
        (item.key !== 'context-graphs' || isCartographerEnabled) &&
        (item.key === 'automations-catalog'
          ? isConsistentLayout
          : !isConsistentLayout ||
            (item.key !== 'drafts' && item.key !== 'automations')),
    ),
  }));

  const {
    isHoverOverlay,
    handleMouseEnter,
    handleMouseLeave,
    handleBackgroundClick,
    handleTriggerClick,
    handleDropdownOpenChange,
  } = useSidebarHoverOverlay(isCollapsed, sidebarRef);

  return (
    <>
      <Sidebar
        ref={sidebarRef}
        collapsible="icon"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleBackgroundClick}
        data-overlay={isHoverOverlay ? 'true' : undefined}
        data-sidebar-variant={variant}
        className={cn(
          'app-sidebar',
          isHoverOverlay && 'fixed left-0 top-0 z-50 shadow-lg',
        )}
      >
        <SidebarHeader className="items-start gap-4 p-0">
          <SidebarBrandRow
            isCollapsed={isCollapsed}
            isHoverOverlay={isHoverOverlay}
            onTriggerClick={handleTriggerClick}
          />
        </SidebarHeader>

        {!isOnboardingOrInvitationPage && (
          <SidebarContent
            data-testid="app-sidebar-content"
            // `no-scrollbar` is a custom global utility (see src/index.css), not a built-in Tailwind class.
            className="no-scrollbar"
          >
            {navGroups.map(group => (
              <SidebarGroup key={group.key} className="px-2.5 py-1.5">
                <SidebarGroupHeading>{t(group.titleKey)}</SidebarGroupHeading>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.key === 'workspace' && (
                      <WorkspaceSwitcher
                        isCollapsed={isCollapsed}
                        onDropdownOpenChange={handleDropdownOpenChange}
                      />
                    )}
                    {group.items.map(item => (
                      <SidebarNavItem
                        key={item.key}
                        item={item}
                        isCollapsed={isCollapsed}
                        url={getRoutePath(item.routeKey, {
                          orgId: currentOrganizationId,
                          workspaceId: currentWorkspaceId,
                        })}
                        activeUrls={item.activeUnder?.map(routeKey =>
                          getRoutePath(routeKey, {
                            orgId: currentOrganizationId,
                            workspaceId: currentWorkspaceId,
                          }),
                        )}
                      />
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          </SidebarContent>
        )}

        {!isOnboardingOrInvitationPage && isNotificationsEnabled && (
          <SidebarGroup className="px-2.5 py-1.5">
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <NotificationCenter
                    isCollapsed={isCollapsed}
                    onDropdownOpenChange={handleDropdownOpenChange}
                  />
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        <SidebarFooter className="px-2.5 pt-2 pb-2.5">
          <UserDropdown
            variant={variant}
            onDropdownOpenChange={handleDropdownOpenChange}
          />
        </SidebarFooter>
      </Sidebar>

      {/* Guard against making organizations API call if user is on onboarding or invitation page */}
      {!isOnboardingOrInvitationPage && <OrganizationSwitcher />}
    </>
  );
}
